import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import NotificationBell from '../components/NotificationBell';
import { 
  Users, 
  Database, 
  FileText, 
  TrendingUp, 
  Shield, 
  Share2,
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  BarChart3,
  Microscope,
  BookOpen,
  Lock,
  Video,
  UserPlus,
  Languages,
  FolderKanban,
  MessageSquare,
  MessageCircle,
  Eye,
  Heart,
  ThumbsUp,
  UserSearch,
  Megaphone,
  ArrowRight,
  User
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
  "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
  "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde",
  "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
  "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia",
  "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece",
  "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary",
  "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica",
  "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos",
  "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
  "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania",
  "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco",
  "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua",
  "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau",
  "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
  "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
  "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia",
  "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka",
  "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania",
  "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey",
  "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom",
  "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
  "Yemen", "Zambia", "Zimbabwe"
];

const LandingPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { login, signup, user, logout } = useAuth();
  const { t, toggleLanguage, language, isRTL } = useLanguage();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [loginOpen, setLoginOpen] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProjects: 0,
    totalDocuments: 0,
    totalConnections: 0
  });
  const [authForm, setAuthForm] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    country: '',
    city: '',
    university: '',
    user_type: '',
    phone: '',
    researchInterests: '',
    profileImage: null,
    google_scholar_url: '',
    orcid_id: ''
  });
  const [isYearly, setIsYearly] = useState(false);
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);

  // Fetch recent forum posts
  useEffect(() => {
    const fetchRecentPosts = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/forum/posts`);
        if (response.ok) {
          const data = await response.json();
          // Get 3 most recent posts sorted by created_at
          const sortedPosts = data
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 3);
          setRecentPosts(sortedPosts);
        }
      } catch (error) {
        console.error('Error fetching recent posts:', error);
      }
    };
    fetchRecentPosts();
  }, [BACKEND_URL]);

  // Fetch recent announcements
  useEffect(() => {
    const fetchRecentAnnouncements = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/announcements`);
        if (response.ok) {
          const data = await response.json();
          // Get 2 most recent (already sorted by backend)
          setRecentAnnouncements(data.slice(0, 2));
        }
      } catch (error) {
        console.error('Error fetching recent announcements:', error);
      }
    };
    fetchRecentAnnouncements();
  }, [BACKEND_URL]);

  useEffect(() => {
    // Track visitor
    const trackVisitor = async () => {
      try {
        await fetch(`${BACKEND_URL}/api/track-visitor`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        console.error('Error tracking visitor:', error);
      }
    };

    // Fetch platform statistics
    const fetchStats = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/stats`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    trackVisitor();
    fetchStats();
  }, []);

  // Redirect admin users to admin panel
  useEffect(() => {
    if (user && user.role === 'admin' && !window.location.pathname.startsWith('/admin')) {
      navigate('/admin');
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const result = await login(authForm.email, authForm.password);
    
    if (result.success) {
      toast({
        title: t('loginSuccessful'),
        description: t('welcomeBack'),
      });
      setLoginOpen(false);
      setAuthForm({ name: '', email: '', password: '' });
      
      // Redirect to dashboard after successful login
      navigate('/dashboard');
    } else {
      toast({
        title: t('loginFailed'),
        description: result.error,
        variant: "destructive"
      });
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    const result = await signup(authForm.name, authForm.email, authForm.password);
    
    if (result.success) {
      // Pre-fill profile form with signup data
      setProfileForm({
        ...profileForm,
        name: authForm.name,
        email: authForm.email
      });
      
      // Close login dialog and open profile creation
      setLoginOpen(false);
      setProfileOpen(true);
      setAuthForm({ name: '', email: '', password: '' });
      
      toast({
        title: "Account Created",
        description: "Please complete your researcher profile to continue.",
      });
    } else {
      toast({
        title: "Signup Failed",
        description: result.error,
        variant: "destructive"
      });
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });

      const data = await response.json();

      toast({
        title: "Success",
        description: data.message || "Password reset link sent to your email"
      });
      
      setForgotPasswordOpen(false);
      setResetEmail('');
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Check if user is logged in
      if (!user || !user.id) {
        toast({
          title: "Login Required",
          description: "Please sign in to create a profile",
          variant: "destructive"
        });
        return;
      }
      
      // Validate user_type is selected
      if (!profileForm.user_type) {
        toast({
          title: t('roleRequired'),
          description: t('selectRoleMessage'),
          variant: "destructive"
        });
        return;
      }
      
      // Check if user already has a profile
      try {
        const checkResponse = await fetch(`${BACKEND_URL}/api/researchers/user/${user.id}`);
        if (checkResponse.ok) {
          // User already has a profile
          toast({
            title: "Profile Exists",
            description: "You already have a researcher profile. You can only create one profile per account.",
            variant: "destructive"
          });
          setProfileOpen(false);
          return;
        }
      } catch (error) {
        // 404 means no profile exists, which is fine - continue with creation
      }
      
      // Profile image is optional, so we can submit without it
      const profileData = {
        name: profileForm.name,
        email: profileForm.email,
        country: profileForm.country,
        city: profileForm.city,
        university: profileForm.university,
        user_type: profileForm.user_type,
        phone: profileForm.phone || null,
        researchInterests: profileForm.researchInterests,
        profileImage: profileForm.profileImage || null,
        google_scholar_url: profileForm.google_scholar_url || null,
        orcid_id: profileForm.orcid_id || null,
        user_id: user.id  // Link profile to user account
      };
      
      const response = await fetch(`${BACKEND_URL}/api/researchers/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData)
      });
      
      if (response.ok) {
        toast({
          title: "Profile Created",
          description: "Your researcher profile has been created successfully!",
        });
        setProfileOpen(false);
        setProfileForm({
          name: '',
          email: '',
          country: '',
          city: '',
          university: '',
          user_type: '',
          phone: '',
          researchInterests: '',
          profileImage: null,
          google_scholar_url: '',
          orcid_id: ''
        });
        // Navigate to dashboard
        navigate('/dashboard');
      } else {
        // Parse error response
        let errorMessage = "Failed to create profile. Please try again.";
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // If parsing fails, use default message
          console.error('Error parsing response:', e);
        }
        
        toast({
          title: "Profile Creation Failed",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error creating profile:', error);
      toast({
        title: "Error",
        description: error.message || "An error occurred while creating your profile.",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    logout();
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm)
      });
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        data = {};
      }
      
      if (response.ok) {
        toast({
          title: "Message Sent",
          description: data.message || "We'll get back to you within 24 hours.",
        });
        setContactForm({ name: '', email: '', message: '' });
      } else {
        // Provide more helpful error message
        const errorMessage = data.detail || response.statusText || 'Failed to send message';
        
        // Check if it's the SendGrid verification issue
        if (response.status === 500 && errorMessage.includes('Forbidden')) {
          toast({
            title: "Email Service Issue",
            description: "Our email service is being configured. Please contact us directly at support@rc-platform.com",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Error",
            description: errorMessage,
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please email us directly at support@rc-platform.com",
        variant: "destructive"
      });
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/researchers/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Profile Created Successfully",
          description: "Redirecting to researcher profiles...",
        });
        setProfileForm({
          name: '',
          email: '',
          country: '',
          city: '',
          university: '',
          user_type: '',
          phone: '',
          researchInterests: '',
          profileImage: null,
          google_scholar_url: '',
          orcid_id: ''
        });
        setProfileOpen(false);
        // Navigate to profiles page after a short delay
        setTimeout(() => {
          navigate('/profiles');
        }, 1500);
      } else {
        throw new Error(data.detail || 'Failed to create profile');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create profile. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
              <img 
                src="https://customer-assets.emergentagent.com/job_knowledgelink-1/artifacts/8zfj25zj_IMG_6196.png" 
                alt="RCP Logo" 
                className="h-12 w-12 object-contain"
              />
            </div>
            <div className={`hidden md:flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-8`}>
              <a href="#about" className="text-gray-600 hover:text-cyan-600 transition-colors">{t('about')}</a>
              <a href="#services" className="text-gray-600 hover:text-cyan-600 transition-colors">{t('services')}</a>
              <button 
                onClick={() => {
                  if (!user) {
                    toast({
                      title: "Login Required",
                      description: "Please sign in and create a researcher profile to access the forum",
                      variant: "destructive"
                    });
                    setLoginOpen(true);
                  } else {
                    navigate('/forum');
                  }
                }}
                className="text-gray-600 hover:text-cyan-600 transition-colors"
              >
                {t('communityForum')}
              </button>
              <a href="#profiles" className="text-gray-600 hover:text-cyan-600 transition-colors">{t('createProfile')}</a>
              <a href="#pricing" className="text-gray-600 hover:text-cyan-600 transition-colors">{t('pricing')}</a>
              <a href="#contact" className="text-gray-600 hover:text-cyan-600 transition-colors">{t('contact')}</a>
            </div>
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
              {/* Language Toggle */}
              <Button
                variant="outline"
                size="sm"
                onClick={toggleLanguage}
                className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
              >
                <Languages className="h-4 w-4 mr-2" />
                {language === 'en' ? 'العربية' : 'English'}
              </Button>
              
              {/* Notification Bell (only shown when logged in) */}
              {user && <NotificationBell />}
              
              <Dialog open={loginOpen} onOpenChange={(open) => {
                setLoginOpen(open);
                if (!open) {
                  setIsSignup(false);
                  setAuthForm({ name: '', email: '', password: '' });
                }
              }}>
              <DialogTrigger asChild>
                {user ? (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">{t('welcome')}, {user.name}!</span>
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/dashboard')}
                      className="border-cyan-500 text-cyan-600"
                    >
                      {t('dashboard')}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleLogout}
                      className="border-cyan-500 text-cyan-600"
                    >
                      {t('logout')}
                    </Button>
                  </div>
                ) : (
                  <Button className="bg-cyan-500 hover:bg-cyan-600 text-white">
                    <Lock className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                    {t('login')}
                  </Button>
                )}
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{isSignup ? t('createAccountTitle') : t('loginTitle')}</DialogTitle>
                  <DialogDescription>
                    {isSignup 
                      ? t('createAccountDesc')
                      : t('loginDesc')}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={isSignup ? handleSignup : handleLogin} className="space-y-4 mt-4">
                  {isSignup && (
                    <div className="space-y-2">
                      <Label htmlFor="auth-name">{t('name')}</Label>
                      <Input 
                        id="auth-name" 
                        type="text" 
                        placeholder={t('namePlaceholder')}
                        value={authForm.name}
                        onChange={(e) => setAuthForm({...authForm, name: e.target.value})}
                        required 
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="auth-email">{t('email')}</Label>
                    <Input 
                      id="auth-email" 
                      type="email" 
                      placeholder={t('emailPlaceholderAuth')}
                      value={authForm.email}
                      onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="auth-password">{t('password')}</Label>
                    <Input 
                      id="auth-password" 
                      type="password"
                      placeholder={isSignup ? t('passwordPlaceholderCreate') : t('passwordPlaceholderEnter')}
                      value={authForm.password}
                      onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                      required 
                    />
                  </div>
                  {!isSignup && (
                    <div className="flex items-center justify-between">
                      <button 
                        type="button"
                        onClick={() => {
                          setLoginOpen(false);
                          setForgotPasswordOpen(true);
                        }}
                        className="text-sm text-cyan-600 hover:underline"
                      >
                        {t('forgotPassword')}
                      </button>
                    </div>
                  )}
                  <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600">
                    {isSignup ? t('signUpBtn') : t('loginBtn')}
                  </Button>
                  <p className="text-center text-sm text-gray-600">
                    {isSignup ? t('alreadyHaveAccount') : t('dontHaveAccount')}
                    <button 
                      type="button"
                      onClick={() => setIsSignup(!isSignup)} 
                      className="text-cyan-600 hover:underline"
                    >
                      {isSignup ? t('loginLink') : t('signUpLink')}
                    </button>
                  </p>
                </form>
              </DialogContent>
            </Dialog>

            {/* Profile Creation Dialog */}
            <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('completeResearcherProfile')}</DialogTitle>
                  <DialogDescription>
                    {t('completeProfileDesc')}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateProfile} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="profile-name">{t('fullNameRequired')}</Label>
                      <Input 
                        id="profile-name" 
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profile-email">{t('emailRequired')}</Label>
                      <Input 
                        id="profile-email" 
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                        required 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="profile-country">{t('countryRequired')}</Label>
                      <Select
                        value={profileForm.country}
                        onValueChange={(value) => setProfileForm({...profileForm, country: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profile-city">{t('cityRequired')}</Label>
                      <Input 
                        id="profile-city" 
                        value={profileForm.city}
                        onChange={(e) => setProfileForm({...profileForm, city: e.target.value})}
                        required 
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="profile-university">{t('universityInstitution')}</Label>
                    <Input 
                      id="profile-university" 
                      value={profileForm.university}
                      onChange={(e) => setProfileForm({...profileForm, university: e.target.value})}
                      required 
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="profile-user-type">{t('iAm')} *</Label>
                    <Select
                      value={profileForm.user_type}
                      onValueChange={(value) => setProfileForm({...profileForm, user_type: value})}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('selectYourRole')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Researcher">{t('researcher')}</SelectItem>
                        <SelectItem value="Research Assistant">{t('researchAssistant')}</SelectItem>
                        <SelectItem value="Both">{t('both')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="profile-phone">{t('phoneOptionalShort')}</Label>
                    <Input 
                      id="profile-phone" 
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="profile-google-scholar">{t('googleScholarOptional')}</Label>
                      <Input 
                        id="profile-google-scholar" 
                        type="url"
                        value={profileForm.google_scholar_url}
                        onChange={(e) => setProfileForm({...profileForm, google_scholar_url: e.target.value})}
                        placeholder={t('googleScholarPlaceholder')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profile-orcid">{t('orcidOptional')}</Label>
                      <Input 
                        id="profile-orcid" 
                        type="text"
                        value={profileForm.orcid_id}
                        onChange={(e) => setProfileForm({...profileForm, orcid_id: e.target.value})}
                        placeholder={t('orcidPlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="profile-interests">{t('researchInterestsSkills')} *</Label>
                    <Textarea 
                      id="profile-interests" 
                      value={profileForm.researchInterests}
                      onChange={(e) => setProfileForm({...profileForm, researchInterests: e.target.value})}
                      rows={4}
                      placeholder={t('describeInterestsSkills')}
                      required 
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1 bg-cyan-500 hover:bg-cyan-600">
                      {t('createProfileBtn')}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setProfileOpen(false)}>
                      {t('cancel')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Forgot Password Dialog */}
            <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reset Your Password</DialogTitle>
                  <DialogDescription>
                    Enter your email address and we'll send you a link to reset your password
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input 
                      id="reset-email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600">
                    Send Reset Link
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setForgotPasswordOpen(false);
                      setLoginOpen(true);
                    }}
                    className="w-full text-sm text-gray-600 hover:text-gray-900"
                  >
                    Back to Login
                  </button>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero / About Section */}
      <section id="about" className="pt-32 pb-20 px-6">
        <div className="container mx-auto">
          <div className="space-y-12">
            {/* Centered Title */}
            <div className="text-center space-y-6">
              <h1 className="text-6xl md:text-7xl font-bold text-cyan-600 leading-tight tracking-tight">
                {t('platformTitle')}
              </h1>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                {t('heroTitle')}{t('heroTitleAccent')}
              </h2>
            </div>
            
            {/* Content Grid */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <p className="text-xl text-gray-600 leading-relaxed">
                  {t('heroDescription')}
                </p>
              </div>
              <div className="relative w-full max-w-md mx-auto">
                <div className="absolute inset-0 bg-cyan-500/10 rounded-3xl transform rotate-3"></div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_b199021f-34ea-4ef3-9b84-c0dad792769d/artifacts/ho17z9wv_Picture13.png" 
                  alt="Laboratory research equipment and scientific apparatus" 
                  className="relative rounded-3xl shadow-2xl w-full aspect-square object-cover"
                />
              </div>
            </div>

            {/* Statistics Section */}
            <div className="pt-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-cyan-600">{stats.totalUsers || 0}</div>
                  <div className="text-gray-600 text-lg">{t('registeredUsers')}</div>
                </div>
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-cyan-600">{stats.totalProjects || 0}</div>
                  <div className="text-gray-600 text-lg">{t('researchProjects')}</div>
                </div>
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-cyan-600">{stats.totalVisitors || 0}</div>
                  <div className="text-gray-600 text-lg">{t('visitors')}</div>
                </div>
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-cyan-600">{stats.totalConnections || 0}</div>
                  <div className="text-gray-600 text-lg">{t('collaborations')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Dashboard Section */}
      <section id="dashboard" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('dashboardTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('dashboardDescription')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 border-transparent hover:border-cyan-500 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <Database className="h-6 w-6 text-cyan-600" />
                </div>
                <CardTitle className="text-xl">{t('dataManagement')}</CardTitle>
                <CardDescription className="text-base">
                  {t('dataManagementDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 border-transparent hover:border-cyan-500 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-cyan-600" />
                </div>
                <CardTitle className="text-xl">{t('teamCollaboration')}</CardTitle>
                <CardDescription className="text-base">
                  {t('teamCollaborationDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 border-transparent hover:border-cyan-500 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-cyan-600" />
                </div>
                <CardTitle className="text-xl">{t('analyticsInsights')}</CardTitle>
                <CardDescription className="text-base">
                  {t('analyticsInsightsDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 border-transparent hover:border-cyan-500 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-cyan-600" />
                </div>
                <CardTitle className="text-xl">{t('documentManagement')}</CardTitle>
                <CardDescription className="text-base">
                  {t('documentManagementDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 border-transparent hover:border-cyan-500 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-cyan-600" />
                </div>
                <CardTitle className="text-xl">{t('progressTracking')}</CardTitle>
                <CardDescription className="text-base">
                  {t('progressTrackingDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 border-transparent hover:border-cyan-500 transition-all duration-300 hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-cyan-600" />
                </div>
                <CardTitle className="text-xl">{t('securityPrivacy')}</CardTitle>
                <CardDescription className="text-base">
                  {t('securityPrivacyDesc')}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('servicesTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('servicesDescription')}
            </p>
          </div>

          {/* First row - 3 cards */}
          <div className="grid lg:grid-cols-3 gap-8 mb-8">
            <Card 
              className="overflow-hidden border-2 hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
              onClick={() => {
                if (!user) {
                  toast({
                    title: "Login Required",
                    description: "Please sign in to access research project management",
                    variant: "destructive"
                  });
                  setLoginOpen(true);
                } else {
                  navigate('/projects');
                }
              }}
            >
              <div className="h-48 bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
                <FolderKanban className="h-24 w-24 text-white/90" />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <FolderKanban className="h-6 w-6 text-cyan-600" />
                  {t('researchProjectManagement')}
                </CardTitle>
                <CardDescription className="text-base">
                  {t('researchProjectManagementDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('fileSharingCollab')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('projectTimeline')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('onlineDocEditing')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('accessTimeTracking')}
                  </li>
                </ul>
                {user && (
                  <Button 
                    className="w-full mt-4 bg-cyan-500 hover:bg-cyan-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/projects');
                    }}
                  >
                    {t('manageYourProjects')}
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card 
              className="overflow-hidden border-2 hover:shadow-2xl transition-shadow duration-300 cursor-pointer hover:border-cyan-500"
              onClick={() => {
                if (!user) {
                  toast({
                    title: "Login Required",
                    description: "Please sign in to access researcher profiles and connect with peers",
                    variant: "destructive"
                  });
                  setLoginOpen(true);
                } else {
                  navigate('/profiles');
                }
              }}
            >
              <div className="h-48 bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
                <UserPlus className="h-24 w-24 text-white/90" />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <UserPlus className="h-6 w-6 text-cyan-600" />
                  {t('findResearchPartner')}
                </CardTitle>
                <CardDescription className="text-base">
                  {t('findResearchPartnerDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('smartMatching')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('browseInterests')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('directMessaging')}
                  </li>
                </ul>
                <Button className="w-full mt-4 bg-cyan-500 hover:bg-cyan-600">
                  {t('browseResearchers')}
                </Button>
              </CardContent>
            </Card>

            <Card 
              className="overflow-hidden border-2 hover:shadow-2xl transition-shadow duration-300 cursor-pointer hover:border-cyan-500"
              onClick={() => {
                if (!user) {
                  toast({
                    title: "Login Required",
                    description: "Please sign in to browse research assistants",
                    variant: "destructive"
                  });
                  setLoginOpen(true);
                } else {
                  navigate('/research-assistants');
                }
              }}
            >
              <div className="h-48 bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
                <UserSearch className="h-24 w-24 text-white/90" />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <UserSearch className="h-6 w-6 text-cyan-600" />
                  {t('findResearchAssistant')}
                </CardTitle>
                <CardDescription className="text-base">
                  {t('findResearchAssistantDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('browseQualifiedAssistants')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('viewSkillsExperience')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('directCollaboration')}
                  </li>
                </ul>
                <Button className="w-full mt-4 bg-cyan-500 hover:bg-cyan-600">
                  {t('browseResearchAssistants')}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Second row - 3 cards */}
          <div className="grid lg:grid-cols-3 gap-8">
            <Card 
              className="overflow-hidden border-2 hover:shadow-2xl transition-shadow duration-300 cursor-pointer hover:border-cyan-500"
              onClick={() => {
                if (!user) {
                  toast({
                    title: "Login Required",
                    description: "Please sign in to access the research library",
                    variant: "destructive"
                  });
                  setLoginOpen(true);
                } else {
                  navigate('/library');
                }
              }}
            >
              <div className="h-48 bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center">
                <BookOpen className="h-24 w-24 text-white/90" />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <BookOpen className="h-6 w-6 text-cyan-600" />
                  {t('researcherLibrary')}
                </CardTitle>
                <CardDescription className="text-base">
                  {t('researcherLibraryDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('pdfUploadManagement')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('aiPoweredSummarization')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('citationGeneration')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('paperAnalysisInsights')}
                  </li>
                </ul>
                {user && (
                  <Button 
                    className="w-full mt-4 bg-cyan-500 hover:bg-cyan-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/library');
                    }}
                  >
                    {t('openLibrary')}
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card 
              className="overflow-hidden border-2 hover:shadow-2xl transition-shadow duration-300 cursor-pointer hover:border-cyan-500"
              onClick={() => {
                if (!user) {
                  toast({
                    title: "Login Required",
                    description: "Please sign in to access virtual discussion rooms",
                    variant: "destructive"
                  });
                  setLoginOpen(true);
                } else {
                  navigate('/virtual-rooms');
                }
              }}
            >
              <div className="h-48 bg-gradient-to-br from-cyan-400 to-cyan-700 flex items-center justify-center">
                <Video className="h-24 w-24 text-white/90" />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Video className="h-6 w-6 text-cyan-600" />
                  {t('virtualDiscussionRooms')}
                </CardTitle>
                <CardDescription className="text-base">
                  {t('virtualDiscussionRoomsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('hdVideo')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('screenSharing')}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('interactiveWhiteboard')}
                  </li>
                </ul>
                <Button className="w-full mt-4 bg-cyan-500 hover:bg-cyan-600">
                  {t('joinVirtualRooms')}
                </Button>
              </CardContent>
            </Card>

            {/* Team Matching Card */}
            <Card 
              className="overflow-hidden border-2 hover:shadow-2xl transition-shadow duration-300 cursor-pointer hover:border-cyan-500"
              onClick={() => {
                if (!user) {
                  toast({
                    title: "Login Required",
                    description: "Please sign in to find your research team",
                    variant: "destructive"
                  });
                  setLoginOpen(true);
                } else {
                  navigate('/team-matching');
                }
              }}
            >
              <div className="h-48 bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
                <Users className="h-24 w-24 text-white/90" />
              </div>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Users className="h-6 w-6 text-cyan-600" />
                  {t('findYourResearchTeamTitle') || 'Find Your Research Team'}
                  <Badge className="ml-2 bg-cyan-100 text-cyan-700 text-xs">AI</Badge>
                </CardTitle>
                <CardDescription className="text-base">
                  {t('aiPoweredMatchingDescription') || 'AI-powered matching system to connect you with researchers sharing your interests'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('semanticInterestMatching') || 'Semantic interest matching'}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('complementaryExpertise') || 'Complementary expertise'}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('smartRecommendations') || 'Smart recommendations'}
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="h-4 w-4 text-cyan-600" />
                    {t('privacyControlledVisibility') || 'Privacy-controlled visibility'}
                  </li>
                </ul>
                {user && (
                  <Button 
                    className="w-full mt-4 bg-cyan-500 hover:bg-cyan-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/team-matching');
                    }}
                  >
                    {t('discoverMatches') || 'Discover Matches'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Community Forum Section */}
      <section id="forum" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('communityForum')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('joinVibrantCommunity')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Recent Announcements Card */}
            <div className="relative">
              <Card className="border-2 border-cyan-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-cyan-50 to-cyan-100">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl">{t('recentAnnouncements')}</CardTitle>
                    <Badge className="bg-cyan-600 text-white">{t('new')}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {recentAnnouncements && recentAnnouncements.length > 0 ? (
                    <div className="space-y-4">
                      {recentAnnouncements.map((announcement, index) => (
                        <div 
                          key={announcement.id || index}
                          className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-cyan-300 transition-colors cursor-pointer"
                          onClick={() => navigate('/announcements')}
                        >
                          {announcement.image_url && (
                            <div className="mb-3 bg-gray-50 rounded-lg p-2 flex items-center justify-center">
                              <img 
                                src={`${BACKEND_URL}${announcement.image_url}`}
                                alt={announcement.title}
                                className="rounded-lg max-h-32 w-auto object-contain"
                              />
                            </div>
                          )}
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-cyan-100 text-cyan-800 text-xs">
                              <Megaphone className="h-3 w-3 mr-1" />
                              {t('announcement')}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {new Date(announcement.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </span>
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {announcement.title}
                          </h4>
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                            {announcement.content}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <User className="h-4 w-4" />
                            <span>{announcement.author_name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">{t('noAnnouncementsYet')}</p>
                    </div>
                  )}
                  <Button
                    className="w-full mt-4 gap-2 bg-cyan-600 hover:bg-cyan-700"
                    onClick={() => navigate('/announcements')}
                  >
                    <Megaphone className="mr-2 h-5 w-5" />
                    {t('viewAllAnnouncements')}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Recent Discussions Card */}
            <div className="relative">
              <Card className="border-2 border-cyan-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-cyan-50 to-cyan-100">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl">{t('recentDiscussions')}</CardTitle>
                    <Badge className="bg-cyan-600 text-white">{t('live')}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {recentPosts && recentPosts.length > 0 ? (
                    <div className="space-y-4">
                      {recentPosts.map((post, index) => (
                        <div 
                          key={post.id || index}
                          className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-cyan-300 transition-colors cursor-pointer"
                          onClick={() => {
                            if (!user) {
                              toast({
                                title: "Login Required",
                                description: "Please sign in and create a researcher profile to access the forum",
                                variant: "destructive"
                              });
                              setLoginOpen(true);
                            } else {
                              navigate(`/forum/${post.id}`);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-cyan-100 text-cyan-800 text-xs capitalize">{post.category}</Badge>
                            <span className="text-xs text-gray-500">
                              {new Date(post.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </span>
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {post.title}
                          </h4>
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                            {post.content}
                          </p>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <MessageCircle className="h-4 w-4" />
                              {post.answer_count || 0} {t('answers')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="h-4 w-4" />
                              {post.views || 0} {t('views')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <MessageSquare className="h-12 w-12 text-cyan-600 mx-auto mb-4" />
                      <p className="text-gray-600 mb-6">
                        {t('beFirstToDiscuss')}
                      </p>
                    </div>
                  )}

                  <Button 
                    className="w-full mt-6 bg-cyan-600 hover:bg-cyan-700"
                    onClick={() => {
                      if (!user) {
                        toast({
                          title: "Login Required",
                          description: "Please sign in and create a researcher profile to access the forum",
                          variant: "destructive"
                        });
                        setLoginOpen(true);
                      } else {
                        navigate('/forum');
                      }
                    }}
                  >
                    <MessageSquare className="mr-2 h-5 w-5" />
                    {t('joinTheDiscussion')}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        
        </div>
      </section>

      {/* Researcher Profile Section */}
      <section id="profiles" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('createProfile')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('createProfileDesc')}
            </p>
          </div>

          {!user ? (
            <Card className="border-2">
              <CardContent className="py-12 text-center">
                <h3 className="text-2xl font-bold mb-4">{t('signInRequired')}</h3>
                <p className="text-gray-600 mb-6">
                  {t('signInToCreateProfile')}
                </p>
                <Button 
                  size="lg"
                  className="bg-cyan-500 hover:bg-cyan-600"
                  onClick={() => setLoginOpen(true)}
                >
                  <Lock className="mr-2 h-5 w-5" />
                  {t('signInToCreateProfileBtn')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">{t('buildProfile')}</CardTitle>
                <CardDescription>{t('buildProfileDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                {/* Profile Photo Upload */}
                <div className="space-y-2">
                  <Label htmlFor="profile-photo">{t('profilePhotoOptional')}</Label>
                  <Input 
                    id="profile-photo" 
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Check file size (max 5MB)
                        if (file.size > 5 * 1024 * 1024) {
                          toast({
                            title: "Error",
                            description: "Image size should be less than 5MB",
                            variant: "destructive"
                          });
                          e.target.value = null;
                          return;
                        }
                        
                        // Convert to base64
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setProfileForm({...profileForm, profileImage: reader.result});
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="cursor-pointer"
                  />
                  <p className="text-sm text-gray-500">{t('uploadPhotoHelper')}</p>
                  {profileForm.profileImage && (
                    <div className="mt-2">
                      <img 
                        src={profileForm.profileImage} 
                        alt="Preview" 
                        className="w-24 h-24 rounded-full object-cover border-2 border-cyan-500"
                      />
                    </div>
                  )}
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="profile-name">{t('fullNameRequired')}</Label>
                    <Input 
                      id="profile-name" 
                      placeholder={t('namePlaceholder')}
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-email">{t('emailRequired')}</Label>
                    <Input 
                      id="profile-email" 
                      type="email" 
                      placeholder={t('emailPlaceholder')}
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                      required 
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="profile-country">{t('countryRequired')}</Label>
                    <Select
                      value={profileForm.country}
                      onValueChange={(value) => setProfileForm({...profileForm, country: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('countryPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-city">{t('cityRequired')}</Label>
                    <Input 
                      id="profile-city" 
                      placeholder={t('cityPlaceholder')}
                      value={profileForm.city}
                      onChange={(e) => setProfileForm({...profileForm, city: e.target.value})}
                      required 
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-university">{t('universityRequired')}</Label>
                  <Input 
                    id="profile-university" 
                    placeholder={t('universityPlaceholder')}
                    value={profileForm.university}
                    onChange={(e) => setProfileForm({...profileForm, university: e.target.value})}
                    required 
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-user-type">{t('iAm')} *</Label>
                  <Select
                    value={profileForm.user_type}
                    onValueChange={(value) => setProfileForm({...profileForm, user_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectYourRole')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Researcher">{t('researcher')}</SelectItem>
                      <SelectItem value="Research Assistant">{t('researchAssistant')}</SelectItem>
                      <SelectItem value="Both">{t('both')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-phone">{t('phoneOptional')}</Label>
                  <Input 
                    id="profile-phone" 
                    type="tel" 
                    placeholder={t('phonePlaceholder')}
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-interests">{t('researchInterestsSkills')} *</Label>
                  <Textarea 
                    id="profile-interests" 
                    placeholder={t('describeInterestsSkills')}
                    rows={5}
                    value={profileForm.researchInterests}
                    onChange={(e) => setProfileForm({...profileForm, researchInterests: e.target.value})}
                    required 
                  />
                  <p className="text-sm text-gray-500">{t('includeResearchAreas')}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="profile-google-scholar-page">{t('googleScholarOptional')}</Label>
                    <Input 
                      id="profile-google-scholar-page" 
                      type="url"
                      placeholder={t('googleScholarPlaceholder')}
                      value={profileForm.google_scholar_url}
                      onChange={(e) => setProfileForm({...profileForm, google_scholar_url: e.target.value})}
                    />
                    <p className="text-sm text-gray-500">{t('googleScholarHelper')}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-orcid-page">{t('orcidOptional')}</Label>
                    <Input 
                      id="profile-orcid-page" 
                      type="text"
                      placeholder={t('orcidPlaceholder')}
                      value={profileForm.orcid_id}
                      onChange={(e) => setProfileForm({...profileForm, orcid_id: e.target.value})}
                    />
                    <p className="text-sm text-gray-500">{t('orcidHelper')}</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600 flex-1">
                    {t('createProfileBtn')}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                    onClick={() => navigate('/profiles')}
                  >
                    {t('viewAllProfiles')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
          )}

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <Card className="text-center border-cyan-200">
              <CardHeader>
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-cyan-600" />
                </div>
                <CardTitle className="text-lg">{t('getDiscovered')}</CardTitle>
                <CardDescription>
                  {t('getDiscoveredDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="text-center border-cyan-200">
              <CardHeader>
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Share2 className="h-8 w-8 text-cyan-600" />
                </div>
                <CardTitle className="text-lg">{t('buildNetwork')}</CardTitle>
                <CardDescription>
                  {t('buildNetworkDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="text-center border-cyan-200">
              <CardHeader>
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-cyan-600" />
                </div>
                <CardTitle className="text-lg">{t('growImpact')}</CardTitle>
                <CardDescription>
                  {t('growImpactDesc')}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('pricingTitle')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('pricingDesc')}
            </p>
          </div>

          {/* Monthly/Yearly Toggle */}
          <div className="flex justify-center items-center gap-4 mb-12">
            <span className={`text-lg font-medium ${!isYearly ? 'text-cyan-600' : 'text-gray-600'}`}>
              {t('monthly')}
            </span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className="relative inline-flex h-8 w-14 items-center rounded-full bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
              style={{ backgroundColor: isYearly ? '#06b6d4' : '#d1d5db' }}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  isYearly ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-lg font-medium ${isYearly ? 'text-cyan-600' : 'text-gray-600'}`}>
              {t('yearly')}
            </span>
            {isYearly && (
              <span className="ml-2 px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
                {t('save20')}
              </span>
            )}
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Basic Plan */}
            <Card className="border-2 border-gray-200 hover:border-cyan-500 transition-all duration-300 hover:shadow-xl">
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-2xl font-bold mb-2">{t('basic')}</CardTitle>
                <div className="mt-4">
                  <span className="text-5xl font-bold">$0</span>
                  <span className="text-gray-600 ml-2">/ {isYearly ? t('yearly').toLowerCase() : t('monthly').toLowerCase()}</span>
                  <div className="text-2xl font-semibold text-gray-500 mt-2">0 SAR</div>
                </div>
                <p className="text-gray-600 mt-4">{t('forIndividuals')}</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>50 {t('aiCredits')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>3 {t('researchProjects')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>5 GB {t('storage')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('basicVirtualRooms')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('communitySupport')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('basicDocManagement')}</span>
                  </li>
                </ul>
                <Button 
                  className="w-full bg-gray-100 text-gray-800 hover:bg-gray-200"
                  onClick={() => navigate('/subscription')}
                >
                  {t('getStartedBtn')}
                </Button>
              </CardContent>
            </Card>

            {/* Premium Plan - Popular */}
            <Card className="border-2 border-cyan-500 hover:border-cyan-600 transition-all duration-300 hover:shadow-xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-cyan-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  {t('mostPopular')}
                </span>
              </div>
              <CardHeader className="text-center pb-8 pt-8">
                <CardTitle className="text-2xl font-bold mb-2">{t('premium')}</CardTitle>
                <div className="mt-4">
                  <span className="text-5xl font-bold">${isYearly ? '150' : '15'}</span>
                  <span className="text-gray-600 ml-2">/ {isYearly ? t('yearly').toLowerCase() : t('monthly').toLowerCase()}</span>
                  <div className="text-2xl font-semibold text-gray-500 mt-2">{isYearly ? '562' : '56'} SAR</div>
                </div>
                {isYearly && (
                  <p className="text-sm text-green-600 mt-2">{t('saveYearly')}</p>
                )}
                <p className="text-gray-600 mt-4">{t('forResearchers')}</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-semibold">500 {t('aiCredits')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('tenResearchProjects')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>50 GB {t('storage')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('extendedVirtualRooms')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('prioritySupport')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('advancedAI')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('realtimeCollaboration')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('exportFormats')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('documentSharingVersioning')}</span>
                  </li>
                </ul>
                <Button 
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                  onClick={() => navigate('/subscription')}
                >
                  {t('startPremium')}
                </Button>
              </CardContent>
            </Card>

            {/* Advanced Plan */}
            <Card className="border-2 border-gray-200 hover:border-cyan-500 transition-all duration-300 hover:shadow-xl">
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-2xl font-bold mb-2">{t('advanced')}</CardTitle>
                <div className="mt-4">
                  <span className="text-5xl font-bold">${isYearly ? '320' : '32'}</span>
                  <span className="text-gray-600 ml-2">/ {isYearly ? t('yearly').toLowerCase() : t('monthly').toLowerCase()}</span>
                  <div className="text-2xl font-semibold text-gray-500 mt-2">{isYearly ? '1,200' : '120'} SAR</div>
                </div>
                {isYearly && (
                  <p className="text-sm text-green-600 mt-2">{t('saveYearlyAdvanced')}</p>
                )}
                <p className="text-gray-600 mt-4">{t('forPowerUsers')}</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-semibold">2,500 {t('aiCredits')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('unlimitedProjectsCollaborators')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>500 GB {t('storage')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('unlimitedRoomsRecording')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('support247')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('advancedAnalyticsDashboard')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('apiAccessIntegrations')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('customDataExport')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('enhancedSecurity')}</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('dedicatedSupportChannel')}</span>
                  </li>
                </ul>
                <Button 
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                  onClick={() => navigate('/subscription')}
                >
                  {t('subscribeNow')}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* FAQ or Additional Info */}
          <div className="mt-16 text-center">
            <p className="text-gray-600 mb-4">
              {t('allPlansIncludeStorage')}
            </p>
            <p className="text-sm text-gray-500">
              {t('needCustomPlan')} <a href="#contact" className="text-cyan-600 hover:underline">{t('contactUs')}</a> {t('forEnterpriseSolutions')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t('getInTouch')}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {t('getInTouchDesc')}
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('emailLabel')}</h3>
                    <p className="text-gray-600">support@rc-platform.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="h-6 w-6 text-cyan-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('whatsappLabel')}</h3>
                    <p className="text-gray-600">00966558812144</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="h-6 w-6 text-cyan-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">X</h3>
                    <a 
                      href="https://twitter.com/RCP_2025" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-cyan-600 transition-colors"
                    >
                      @RCP_2025
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">{t('sendMessage')}</CardTitle>
                <CardDescription>{t('sendMessageDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('nameLabel')}</Label>
                    <Input 
                      id="name" 
                      placeholder="Dr. Jane Smith"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">{t('email')}</Label>
                    <Input 
                      id="contact-email" 
                      type="email" 
                      placeholder="jane.smith@university.edu"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">{t('messageLabel')}</Label>
                    <Textarea 
                      id="message" 
                      placeholder={t('messagePlaceholder')}
                      rows={5}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      required 
                    />
                  </div>
                  <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600">
                    {t('sendBtn')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_knowledgelink-1/artifacts/8zfj25zj_IMG_6196.png" 
                  alt="RCP Logo" 
                  className="h-10 w-10 object-contain"
                />
                <span className="text-xl font-bold">RCP</span>
              </div>
              <p className="text-gray-400">
                {t('footerTagline')}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('platform')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#about" className="hover:text-cyan-400 transition-colors">{t('about')}</a></li>
                <li><a href="#dashboard" className="hover:text-cyan-400 transition-colors">{t('dashboard')}</a></li>
                <li><a href="#services" className="hover:text-cyan-400 transition-colors">{t('services')}</a></li>
                <li><a href="#" className="hover:text-cyan-400 transition-colors">{t('pricing')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('support')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button onClick={() => navigate('/faq')} className="hover:text-cyan-400 transition-colors">
                    {language === 'ar' ? 'الأسئلة الشائعة' : 'FAQs'}
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/user-guide')} className="hover:text-cyan-400 transition-colors">
                    {language === 'ar' ? 'دليل المستخدم' : 'User Guide'}
                  </button>
                </li>
                <li><a href="mailto:support@rc-platform.com" className="hover:text-cyan-400 transition-colors">{t('contactSupport')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('legal')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button onClick={() => navigate('/privacy-policy')} className="hover:text-cyan-400 transition-colors">
                    {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/terms')} className="hover:text-cyan-400 transition-colors">
                    {language === 'ar' ? 'شروط الاستخدام' : 'Terms of Use'}
                  </button>
                </li>
                <li><a href="#" className="hover:text-cyan-400 transition-colors">{t('cookiePolicy')}</a></li>
                <li><a href="#" className="hover:text-cyan-400 transition-colors">{t('gdpr')}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>{t('copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;